import { FC } from 'react';
import { KatToggle, KatInput } from '@amzn/katal-react';
import { Tooltip } from '@mui/material';
import { MarketPlace } from '@amzn/fire-tv-campaign-automation-service';
import { AssetState } from 'src/types';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import {
  OVERLAY_FIELDS,
  OVERLAY_FIELDS_CONFIG,
  OverlayToggleTextConfig,
  OverlayToggleDropDownConfig,
} from 'src/configs/overlayConfig';
import styles from './AssetInput.module.css';

interface OverlaySectionProps {
  assetState: AssetState;
  marketplace: MarketPlace;
  enabled: boolean;
  setState: (update: Partial<AssetState>) => void;
}

export const OverlaySection: FC<OverlaySectionProps> = ({
  assetState,
  marketplace,
  enabled,
  setState,
}) => {
  const overlayToggles = assetState.overlayToggles || {};

  const handleToggle = (fieldKey: string) => {
    const newToggles = { ...overlayToggles, [fieldKey]: !overlayToggles[fieldKey] };
    const update: Partial<AssetState> = { overlayToggles: newToggles };
    const currentValue = assetState[fieldKey as keyof AssetState] as string | undefined;
    if (overlayToggles[fieldKey] && currentValue?.trim()) {
      (update as Record<string, unknown>)[fieldKey] = undefined;
    }
    setState(update);
  };

  const renderField = (field: (typeof OVERLAY_FIELDS)[number]) => {
    const isOn = !!overlayToggles[field.key];
    const value = assetState[field.key as keyof AssetState] as string | undefined;
    const config = OVERLAY_FIELDS_CONFIG[field.key as keyof typeof OVERLAY_FIELDS_CONFIG];

    return (
      <div key={field.key} className={styles.overlayField}>
        <div className={styles.overlayToggleRow}>
          <KatToggle
            checked={isOn}
            onChange={() => handleToggle(field.key)}
            disabled={!enabled}
            data-testid={`${config.dataTestId}-toggle`}
          />
          <span className={styles.overlayLabel}>{config.label}</span>
        </div>
        {isOn && field.type === 'toggle-text' && (
          <KatInput
            value={value || ''}
            onInput={(e: { currentTarget: { value: string } }) =>
              setState({ [field.key]: e.currentTarget.value } as Partial<AssetState>)
            }
            placeholder={
              (config as OverlayToggleTextConfig).placeholderByMarketplace?.[marketplace] ||
              config.placeholder
            }
            data-testid={config.dataTestId}
            maxlength={field.maxLength?.toString()}
            disabled={!enabled}
          />
        )}
        {isOn && field.type === 'toggle-dropdown' && (
          <StyledDropdown
            value={value}
            onChange={(e) => setState({ [field.key]: e.detail.value } as Partial<AssetState>)}
            options={
              (config as OverlayToggleDropDownConfig).optionsByMarketplace?.[marketplace] ||
              (config as OverlayToggleDropDownConfig).defaultOptions ||
              []
            }
            placeholder={config.placeholder}
            data-testid={config.dataTestId}
            disabled={!enabled}
          />
        )}
      </div>
    );
  };

  return (
    <Tooltip
      title="Upload and transform a background image to enable overlays"
      disableHoverListener={enabled}
      placement="top"
    >
      <div className={styles.overlaySection}>
        <div className={styles.overlayRow}>
          {renderField(OVERLAY_FIELDS[0])}
          {renderField(OVERLAY_FIELDS[2])}
        </div>
        {renderField(OVERLAY_FIELDS[1])}
      </div>
    </Tooltip>
  );
};
